import type { FieldValue, MatrixAnswers } from '../model/types';

export function createEmptyAnswers(matrixId: number): MatrixAnswers {
  return { version: 1, matrixId, values: {}, tables: {} };
}

export function withFieldValue(
  answers: MatrixAnswers,
  fieldId: string,
  value: FieldValue,
): MatrixAnswers {
  return { ...answers, values: { ...answers.values, [fieldId]: value } };
}

function extraRowsOf(answers: MatrixAnswers, tableId: string): Array<Record<number, FieldValue>> {
  return answers.tables[tableId]?.extraRows ?? [];
}

function withExtraRows(
  answers: MatrixAnswers,
  tableId: string,
  extraRows: Array<Record<number, FieldValue>>,
): MatrixAnswers {
  return { ...answers, tables: { ...answers.tables, [tableId]: { extraRows } } };
}

export function withExtraRowAppended(answers: MatrixAnswers, tableId: string): MatrixAnswers {
  return withExtraRows(answers, tableId, [...extraRowsOf(answers, tableId), {}]);
}

// Blind to which rows are saved: the sheet goes through withNewExtraRowRemoved. Removing the only
// row keeps the empty entry, a valid shape that saves the next append from re-creating it.
export function withExtraRowRemoved(answers: MatrixAnswers, tableId: string, rowIndex: number): MatrixAnswers {
  const rows = extraRowsOf(answers, tableId);
  if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex >= rows.length) return answers;
  return withExtraRows(answers, tableId, rows.filter((_, index) => index !== rowIndex));
}

export function withExtraRowCellValue(
  answers: MatrixAnswers,
  tableId: string,
  rowIndex: number,
  columnIndex: number,
  value: FieldValue,
): MatrixAnswers {
  const rows = extraRowsOf(answers, tableId);
  if (rowIndex < 0 || rowIndex >= rows.length) return answers;
  return withExtraRows(
    answers,
    tableId,
    rows.map((row, index) => (index === rowIndex ? { ...row, [columnIndex]: value } : row)),
  );
}
